import { useForm } from "react-hook-form";
import {
  Button,
  Hairline,
  Input,
  Modal,
  Numeric,
  Textarea,
} from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import type { AdjustStockResponse } from "@wmart/shared-types";

interface AdjustFormValues {
  delta: string;
  reason: string;
}

interface Props {
  variantId: string;
  variantSku: string;
  currentStock: number;
  onSuccess: () => void | Promise<void>;
  onClose: () => void;
}

const REASON_MAX = 500;

/**
 * Adjust a variant's stock level. POST /v1/admin/variants/:id/adjust-stock
 * — body is `AdjustStockBody = { delta, reason }`. `delta` is a non-zero
 * signed integer; the API enforces both `inventory.zero_delta` (422 when
 * delta=0) and `inventory.adjustment_into_negative` (422 when
 * before+delta < 0). Both surface via the toast provider.
 *
 * Rendered inside `<Modal>` (Plan 11 T11) — ESC + click-outside +
 * focus-trap + scroll-lock all come from the primitive.
 */
export function AdjustStockModal({
  variantId,
  variantSku,
  currentStock,
  onSuccess,
  onClose,
}: Props) {
  const { notify, notifyProblem } = useToast();
  const {
    register,
    handleSubmit,
    watch,
    formState: { isSubmitting, errors },
  } = useForm<AdjustFormValues>({
    defaultValues: { delta: "", reason: "" },
  });

  const deltaRaw = watch("delta");
  const reason = watch("reason");

  const parsedDelta = Number(deltaRaw);
  const deltaIsValid =
    deltaRaw !== "" &&
    Number.isInteger(parsedDelta) &&
    parsedDelta !== 0;
  const previewAfter = deltaIsValid ? currentStock + parsedDelta : null;

  async function onSubmit(values: AdjustFormValues) {
    const delta = Number(values.delta);
    if (!Number.isInteger(delta) || delta === 0) {
      notify("error", "Delta must be a non-zero integer");
      return;
    }
    try {
      await api.post<AdjustStockResponse>(
        `/v1/admin/variants/${variantId}/adjust-stock`,
        { delta, reason: values.reason },
      );
      const sign = delta > 0 ? "+" : "";
      notify("success", `Stock adjusted (${sign}${delta})`);
      await onSuccess();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Stock adjustment failed");
    }
  }

  const submitDisabled =
    isSubmitting ||
    !deltaIsValid ||
    reason.trim() === "" ||
    (previewAfter !== null && previewAfter < 0);

  return (
    <Modal
      open
      onClose={onClose}
      title="Adjust stock"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={submitDisabled}
            loading={isSubmitting}
          >
            Adjust stock
          </Button>
        </>
      }
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-4"
      >
        <p className="text-body-sm text-ink-500">
          SKU <span className="font-mono text-ink-700">{variantSku}</span>{" "}
          · current stock{" "}
          <Numeric>{currentStock}</Numeric>
        </p>

        <Input
          type="number"
          step={1}
          label="Adjustment (positive or negative)"
          placeholder="e.g. 5 or -3"
          helper="Positive to add, negative to remove. Cannot be zero."
          error={errors.delta?.message}
          {...register("delta", { required: "Delta is required" })}
        />

        <Textarea
          label="Reason"
          rows={3}
          maxLength={REASON_MAX}
          helper={`${reason.length}/${REASON_MAX}`}
          error={errors.reason?.message}
          {...register("reason", {
            required: "Reason is required",
            minLength: { value: 1, message: "Reason cannot be empty" },
            maxLength: {
              value: REASON_MAX,
              message: `Max ${REASON_MAX} characters`,
            },
          })}
        />

        {previewAfter !== null ? (
          <>
            <Hairline />
            <p
              className={
                previewAfter < 0
                  ? "text-body-sm text-danger"
                  : "text-body-sm text-ink-700"
              }
            >
              After adjustment: <Numeric>{previewAfter}</Numeric>
              {previewAfter < 0 ? " (would be rejected)" : ""}
            </p>
          </>
        ) : null}

        {/* Hidden submit to allow Enter-to-submit while keeping primary CTA in footer. */}
        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </Modal>
  );
}
